/* Crowns: the win condition made visible.

   The rule has always been "lose every one of your initial cities and you are eliminated"
   (backend requirement 06); what nothing said is *which* cities those are. A crown is the
   player-facing name for one of them: up to three per player, frozen from the lobby pick, and a
   player at zero crowns is out — take all of an opponent's crowns to defeat them. A crown that
   falls is gone for good (owner, 2026-10-04): the server keeps each player's `crowns_lost`, and
   retaking the city does not give the crown back (`simulator/use_cases/check_victory.py`). A
   lost crown is drawn faded rather than gone, so the map still says where it stood.

   Everything here is derived from the same state the server decides from — each player's
   `initial_location_list` in `metadata.users` (present in live payloads and in replay snapshots
   alike) with the player payload's `initialLocations` as fallback — so the map and the counters
   can never disagree with the rule. Locations arrive in two shapes: `[col, row]` arrays from the
   metadata JSON, `{column, row}` objects from the player rows; `keyOf` accepts both. */

import { isSurvival } from "src/utils/gameModes"

const keyOf = (location) =>
  Array.isArray(location) ? `${location[0]}-${location[1]}` : `${location.column}-${location.row}`

/* Every crown cell on the board: "col-row" -> { userId, held }. `userId` (a string, like the
   metadata's user keys) is the player whose elimination the cell decides — whoever holds it now —
   and `held` is whether that player still does. */
export const crownCells = (metadata, players = []) => {
  const initialByUser = {}
  const lostByUser = {}
  Object.entries(metadata?.users || {}).forEach(([id, user]) => {
    const list = user?.initial_location_list || []
    if (list.length) initialByUser[id] = list.map(keyOf)
    lostByUser[id] = new Set((user?.crowns_lost || []).map(keyOf))
  })
  // Older snapshots, or a metadata-less payload: the player rows carry the same pick.
  players.forEach((player) => {
    const id = String(player?.user?.id ?? player?.id)
    const list = player?.initialLocations || player?.initial_locations || []
    if (!initialByUser[id] && list.length) initialByUser[id] = list.map(keyOf)
  })

  const cells = {}
  Object.entries(initialByUser).forEach(([userId, keys]) => {
    keys.forEach((key) => {
      const owner = metadata?.cells?.[key]?.owner
      const lost = lostByUser[userId]?.has(key) ?? false
      cells[key] = { userId, held: !lost && owner != null && String(owner) === userId }
    })
  })
  return cells
}

/* Per player: { userId: { held, total } }. `held === 0` is exactly the backend's elimination. */
export const crownStandings = (metadata, players = []) => {
  const standings = {}
  Object.values(crownCells(metadata, players)).forEach(({ userId, held }) => {
    if (!standings[userId]) standings[userId] = { held: 0, total: 0 }
    standings[userId].total += 1
    if (held) standings[userId].held += 1
  })
  return standings
}

/* Who the round is actually waiting on.

   Two exclusions, and each was a counter that lied. An eliminated player never readies again —
   the server's round gate skips them (R2) — so counting them left "1 / 2 ready" standing for ever
   beside somebody with no crowns left (owner, 2026-09-08). And in survival the waves ready
   themselves the moment they land, so counting the bots makes the gauge a weather report: the
   people are the room.

   It lives here, next to the crowns elimination is read off, because the number is shown *twice* —
   the roster's header on the desktop docks, and the phone shell's Forces tab badge and sheet
   subtitle — and the two had their own arithmetic: playing a solo survival game on a phone, the
   tab read "3/4" (three enemy bots, ready, plus me) over a roster that said "0 / 1 LISTOS". */
export const stillPlayingSeats = (game) => {
  const survival = isSurvival(game?.mode)
  const seats = (game?.players || []).filter((seat) => !(survival && seat?.user?.isBot))
  const standings = crownStandings(game?.metadata, game?.players || [])
  return seats.filter((seat) => {
    const crown = standings[String(seat?.user?.id ?? seat?.id)]
    return !(crown != null && crown.held === 0)
  })
}

export const readiness = (game) => {
  const seats = stillPlayingSeats(game)
  return { ready: seats.filter((seat) => seat?.ready).length, total: seats.length }
}
